import { Component, EventEmitter, HostListener, Inject, Input, OnInit, Optional, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { GridColumnModel, GridComponent, SearchSettingsModel, ExcelExportProperties } from '@syncfusion/ej2-angular-grids';
import { Query } from '@syncfusion/ej2-data';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UserProfileService } from '../../../services/user-profile.service';
import { Applications } from 'projects/sales/src/app/shared/models/app.model';
import { CommonService } from 'projects/sales/src/app/shared/services/common.service';
import { getExcelDataSource, gridActionBeginForFilteringOdataV4, gridActionsForFiltering, updateFilterIcon } from 'projects/sales/src/app/shared/models/utils/grid-functions';
import { getCurrentDate } from 'projects/sales/src/app/shared/models/utils/date-functions';
import { UserRoles } from 'projects/sales/src/app/shared/constants/app.constants';

@Component({
    selector: 'app-direct-reports',
    templateUrl: './direct-reports.component.html',
    styleUrls: ['./direct-reports.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DirectReportsComponent implements OnInit {
  isExpanded: boolean = false;
  showGrid: boolean = false;
  popupData: any;
  query: Query;

  gridHeight: any = window.innerHeight / 1.7;


  gridColumns!: GridColumnModel[];
  @ViewChild('columnChooserForGrid') columnChooserForGrid!: any;
  
  popupGrid!: GridComponent;
  pageSettings: any;
  searchSettings?: SearchSettingsModel;

  @ViewChild('popupGrid') set gridComponent(gridComponent: GridComponent) {
    if (gridComponent) {
      this.popupGrid = gridComponent;
      this.popupGrid.toolbar = this.getAddressesGridToolbar();

    }
  }
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.gridHeight = window.innerHeight / 1.7;
  }
  constructor(
    private usersService: UserProfileService,
    private commonService: CommonService,
    @Optional() public dialogRef: MatDialogRef<any>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any) { }


  ngOnInit() {
    this.pageSettings = { pageSizes: [20, 50, 100, 500], pageSize: 20 };
    this.getAddressesGridColumns();
    this.getData(this.dialogData?.app);
  }
  getData(app: string) {
    this.showGrid = false;
    const application = Applications.RimsSales.includes(app) ? Applications.RimsSales : Applications.RimsService;
    this.usersService.getDirectReportsForUserId(application, this.dialogData.userId).subscribe({
      next: (res: any) => {
        this.popupData = res?.result;
        this.showGrid = true;
      },
      error: (err) => {
        this.showGrid = true;
      }
    });
  }
  onRadioChange(e: any) {
    const label = e?.event?.currentTarget?.ej2_instances[0]?.properties?.label;
    this.getData(label);
  }
  getAddressesGridColumns() {
    this.gridColumns = [
      { field: 'employeeId', headerText: 'Employee Id', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true },
      { field: 'name', headerText: 'Name', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
      { field: 'role', headerText: 'Role', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
      { field: 'application', headerText: 'Application', filter: {type: 'Excel'}, type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
      { field: 'email', headerText: 'Email', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
      { field: 'mobile', headerText: 'Contact', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
      { field: 'serviceDepartment', headerText: 'Service Department', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
      { field: 'district', headerText: 'District', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
      { field: 'region', headerText: 'Region', type: 'string', allowFiltering: true, allowEditing: false, textAlign: 'Left', width: 120, visible: true, showInColumnChooser: true },
    ] as GridColumnModel[];
    this.searchSettings = { fields: [ ...this.gridColumns.map(x => x.type != 'date' && x.field) ].filter(
          f => !['Actions', 'mobile', 'role', false].includes(f)
        ) };
  }

  onToolbarClick(args: { item: { id: string; }; }) {
    if (args.item.id === 'excel-export') {
      const dataSource = getExcelDataSource(this.popupGrid);
      let excelExportProperties: ExcelExportProperties = {
        dataSource,
        hierarchyExportMode: 'Expanded',
        theme: {
          header: { bold: true, backColor: '#eeeeee', fontSize: 15 }
        },
        fileName: `Employees (${getCurrentDate()}).xlsx`
      };
      this.popupGrid.excelExport(excelExportProperties);
    } else if (args.item.id === 'clear-filter') {
      this.popupGrid.clearFiltering();
      this.popupGrid.clearSorting();
      this.popupGrid.search('');
      this.popupGrid.refresh();
    }
  }

  onGridActionBegin(args: any, grid?: any) {
      gridActionBeginForFilteringOdataV4(args);
  }

  onActionComplete(args: any, grid: GridComponent) {
    gridActionsForFiltering(args, grid);
    updateFilterIcon(args, this.popupGrid);
  }

  getAddressesGridToolbar(): any {
    let tb = [
      { text: '', id: 'column-chooser', align: 'Left', template: this.columnChooserForGrid, tooltipText: 'Show/Hide Columns' },
      'Search',
      { text: '', id: 'clear-filter', align: 'Right', prefixIcon: 'fas fa-filter', cssClass: 'grid-filter-icon', tooltipText: 'Clear all Filters' }
    ];
    
    if (this.commonService.getUSER()?.role !== UserRoles.salesRep) {
      const indexExcelExport = tb.findIndex(fi => fi == 'Search');
      tb.splice(indexExcelExport + 1, 0, { text: '', id: 'excel-export', align: 'Right', prefixIcon: 'e-excelexport', cssClass: `grid-add-button`, tooltipText: 'Excel Export' })
    }
    return tb;
  }


  expandCollapse() {
    this.isExpanded = !this.isExpanded;
    this.gridHeight = window.innerHeight / 1.7;
    // const factor = this.isExpanded ? 0.8 : 0.6;
    // const bodyHeight = (document.querySelector('div.modal-body') as HTMLElement).offsetHeight;
    // this.gridHeight = bodyHeight * factor;
  }
  close() {
    this.dialogRef.close();
  }


}
